/**
 * Shared service for importing Trello cards / ClickUp tasks into a TaskRush project.
 * Used by ProjectModalContent (create new project + import) and ProjectsList (import into existing project).
 */
import API_CONFIG from '../../../../config/api'

export type TrelloMappingData = {
  board: any
  lists: any[]
  mapping: Record<string, string>
  cards: any[]
}

export type ClickUpMappingData = {
  source: string
  list: any
  tasks: any[]
  mapping: Record<string, string>
  clickup_list_id?: string | null
  clickup_status_mapping?: Record<string, string> | null
}

export type ImportProgress = {
  total: number
  current: number
  currentItem?: string
}

function getAuthToken(): string | null {
  const authData = localStorage.getItem('impersonating-auth-token')
  if (authData) {
    try {
      const parsed = JSON.parse(authData)
      return parsed.api_token
    } catch {
      return null
    }
  }
  return null
}

function mapTrelloStatusToTaskRush(trelloStatus: string): string {
  switch (trelloStatus) {
    case 'Backlog':
      return 'Backlog'
    case 'Em andamento':
      return 'Em andamento'
    case 'Entregue':
      return 'Entregue'
    default:
      return 'Backlog'
  }
}

function buildEnhancedDescription(card: any): string {
  let description = card.desc || ''
  if (card.checklists && card.checklists.length > 0) {
    description += '\n\n📋 **Checklists:**\n'
    card.checklists.forEach((checklist: any) => {
      description += `\n**${checklist.name}:**\n`
      if (checklist.checkItems && checklist.checkItems.length > 0) {
        checklist.checkItems.forEach((item: any) => {
          const status = item.state === 'complete' ? '✅' : '⏳'
          description += `${status} ${item.name}\n`
        })
      } else {
        description += 'Nenhum item encontrado\n'
      }
    })
  }
  if (card.members && card.members.length > 0) {
    description += '\n👥 **Membros:**\n'
    card.members.forEach((member: any) => {
      description += `• ${member.fullName || member.username}\n`
    })
  }
  if (card.attachments && card.attachments.length > 0) {
    description += '\n📎 **Anexos do Trello:**\n'
    card.attachments.forEach((attachment: any) => {
      description += `• ${attachment.name} (${attachment.mimeType || 'tipo desconhecido'})\n`
    })
    description += '\n*Os anexos serão importados automaticamente para o TaskRush*\n'
  }
  return description.trim()
}

function mapTrelloLabelsToPriority(labels: any[]): string {
  if (!labels || labels.length === 0) return 'medium'
  const priorityMap: Record<string, string> = {
    red: 'alta',
    orange: 'alta',
    yellow: 'media',
    green: 'baixa',
    blue: 'media',
    purple: 'baixa',
    pink: 'media',
    sky: 'baixa',
    lime: 'baixa',
    black: 'critica',
    white: 'baixa',
  }
  for (const label of labels) {
    const color = label.color?.toLowerCase()
    const name = label.name?.toLowerCase()
    if (color && priorityMap[color]) return priorityMap[color]
    if (name) {
      if (name.includes('urgente') || name.includes('crítica') || name.includes('alta')) return 'alta'
      if (name.includes('média') || name.includes('media')) return 'media'
      if (name.includes('baixa')) return 'baixa'
    }
  }
  return 'medium'
}

async function processTrelloAttachments(attachments: any[], taskId: number): Promise<void> {
  const token = getAuthToken()
  if (!token) return
  for (const attachment of attachments) {
    try {
      const res = await fetch(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.TRELLO.ATTACHMENTS_DOWNLOAD), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attachment_url: attachment.url,
          attachment_name: attachment.name,
          attachment_mime_type: attachment.mimeType,
          task_id: taskId,
        }),
      })
      if (!res.ok) {
        const err = await res.json()
        console.error(`Erro ao processar anexo ${attachment.name}:`, err)
      }
    } catch (e) {
      console.error(`Erro ao processar anexo ${attachment.name}:`, e)
    }
  }
}

function mapClickUpPriorityToTaskRush(clickUpPriority: any): string {
  if (!clickUpPriority || !clickUpPriority.priority) return 'Normal'
  switch (clickUpPriority.priority.toLowerCase()) {
    case 'urgent':
      return 'critica'
    case 'high':
      return 'alta'
    case 'normal':
      return 'Normal'
    case 'low':
      return 'baixa'
    case 'none':
      return 'media'
    default:
      return 'Normal'
  }
}

function buildClickUpDescription(task: any): string {
  let description = task.text_content || task.description || ''
  if (task.time_spent) {
    let timeSpentHours = 0
    if (typeof task.time_spent === 'string') {
      timeSpentHours = Math.round((parseInt(task.time_spent) / (1000 * 60 * 60)) * 100) / 100
    } else if (typeof task.time_spent === 'number') {
      timeSpentHours = Math.round((task.time_spent / (1000 * 60 * 60)) * 100) / 100
    }
    if (timeSpentHours > 0) description += `\n\n⏱️ **Tempo lançado:** ${timeSpentHours}h`
  }
  if (task.time_estimate) {
    let h = 0
    if (typeof task.time_estimate === 'string') h = Math.round((parseInt(task.time_estimate) / (1000 * 60 * 60)) * 100) / 100
    else if (typeof task.time_estimate === 'number') h = Math.round((task.time_estimate / (1000 * 60 * 60)) * 100) / 100
    if (h > 0) description += `\n\n⏰ **Estimativa de tempo:** ${h}h`
  }
  if (task.points) description += `\n🎯 **Pontos:** ${task.points}`
  if (task.tags && task.tags.length > 0) {
    description += '\n\n🏷️ **Tags:**\n'
    task.tags.forEach((tag: any) => { description += `• ${tag.name}\n` })
  }
  if (task.assignees && task.assignees.length > 0) {
    description += '\n👥 **Responsáveis:**\n'
    task.assignees.forEach((a: any) => { description += `• ${a.username || a.name}\n` })
  }
  if (task.url) description += `\n🔗 **Link ClickUp:** ${task.url}`
  return description.trim()
}

async function processClickUpAttachments(attachments: any[], taskId: number): Promise<void> {
  const token = getAuthToken()
  if (!token) return
  for (const attachment of attachments) {
    try {
      const res = await fetch(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.CLICKUP.ATTACHMENTS_DOWNLOAD), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attachment_url: attachment.url,
          attachment_name: attachment.name,
          attachment_mime_type: attachment.mimeType,
          task_id: taskId,
        }),
      })
      if (!res.ok) {
        const err = await res.json()
        console.error(`Erro ao processar anexo ${attachment.name}:`, err)
      }
    } catch (e) {
      console.error(`Erro ao processar anexo ${attachment.name}:`, e)
    }
  }
}

async function createClickUpTimeTracking(
  taskResult: any,
  timeSpent: number,
  creatorId: string
): Promise<void> {
  const token = getAuthToken()
  if (!token) return
  let taskId: number | null = null
  if (taskResult.tasks?.[0]?.id) taskId = taskResult.tasks[0].id
  else if (taskResult.task?.id) taskId = taskResult.task.id
  else if (taskResult.id) taskId = taskResult.id
  if (taskId == null) return
  const totalTimeSeconds = Math.floor(timeSpent / 1000)
  const res = await fetch(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.CLICKUP.TIME_TRACKING), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      task_id: taskId,
      user_id: creatorId,
      total_time: totalTimeSeconds,
      begin_time: new Date().toISOString(),
      end_time: new Date().toISOString(),
    }),
  })
  if (!res.ok) {
    const err = await res.json()
    console.error('Erro ao criar time tracking:', err)
  }
}

function getCreatorIdFromToken(): string | null {
  const authData = localStorage.getItem('impersonating-auth-token')
  if (!authData) return null
  try {
    const parsed = JSON.parse(authData)
    const token = parsed.api_token
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.sub ?? null
  } catch {
    return null
  }
}

async function importTrelloCard(
  card: any,
  projectId: number,
  trelloMapping: TrelloMappingData
): Promise<void> {
  const token = getAuthToken()
  const creatorId = getCreatorIdFromToken()
  if (!token || !creatorId) {
    console.error('Token ou creatorId não disponível')
    return
  }
  const listId = card.idList
  let status = trelloMapping.mapping?.[listId] ?? 'Backlog'
  const taskStatus = mapTrelloStatusToTaskRush(status)
  const enhancedDescription = buildEnhancedDescription(card)
  const priority = mapTrelloLabelsToPriority(card.labels || [])
  const beginDate = card.start ? new Date(card.start).toISOString().split('T')[0] : null
  const expectedDate = card.due ? new Date(card.due).toISOString().split('T')[0] : null
  const taskData = {
    name: card.name,
    description: enhancedDescription,
    project_id: projectId,
    creator_id: creatorId,
    status: taskStatus,
    priority,
    begin_date: beginDate,
    expected_date: expectedDate,
    time_estimate: card.timeSpent || null,
    points: card.points || null,
  }
  const response = await fetch(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.TASKS.CREATE), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(taskData),
  })
  if (response.ok) {
    const result = await response.json()
    const taskId = result.task?.id ?? result.id
    if (card.attachments?.length > 0 && taskId) {
      await processTrelloAttachments(card.attachments, taskId)
    }
  } else {
    const err = await response.json()
    console.error(`Erro ao criar tarefa ${card.name}:`, err)
  }
}

async function importClickUpTask(
  task: any,
  projectId: number,
  clickUpMapping: ClickUpMappingData
): Promise<void> {
  const token = getAuthToken()
  const creatorId = getCreatorIdFromToken()
  if (!token || !creatorId) {
    console.error('Token ou creatorId não disponível')
    return
  }
  let taskStatus = clickUpMapping.mapping?.[task.id] ?? task.mappedStatus ?? 'Backlog'
  const priority = mapClickUpPriorityToTaskRush(task.priority)
  const enhancedDescription = buildClickUpDescription(task)
  let beginDate: string | null = null
  let expectedDate: string | null = null
  let startDate: string | null = null
  if (task.start_date) {
    try {
      const startDateObj = new Date(parseInt(task.start_date))
      if (!isNaN(startDateObj.getTime())) {
        startDate = startDateObj.toISOString().split('T')[0]
        beginDate = startDate
      }
    } catch {
      // ignore
    }
  }
  if (task.due_date) {
    try {
      const due = new Date(parseInt(task.due_date))
      if (!isNaN(due.getTime())) expectedDate = due.toISOString().split('T')[0]
    } catch {
      // ignore
    }
  }
  if (!beginDate && task.date_created) {
    try {
      const created = new Date(parseInt(task.date_created))
      if (!isNaN(created.getTime())) beginDate = created.toISOString().split('T')[0]
    } catch {
      // ignore
    }
  }
  const timeSpent = task.time_spent ? parseInt(String(task.time_spent)) : null
  const timeSpentHours = timeSpent
    ? Math.round((timeSpent / (1000 * 60 * 60)) * 100) / 100
    : null
  const taskData = {
    name: task.name,
    description: enhancedDescription,
    project_id: projectId,
    creator_id: creatorId,
    status: taskStatus,
    priority,
    begin_date: beginDate,
    expected_date: expectedDate,
    start_date: startDate,
    assignee_id: null,
    time_estimate: timeSpent,
    points: task.points || null,
    priority_id:
      priority === 'critica' ? 1 : priority === 'alta' ? 2 : priority === 'Normal' ? 3 : priority === 'media' ? 4 : priority === 'baixa' ? 5 : 3,
    time_estimate_ms: timeSpent,
    time_estimate_seconds: timeSpent ? Math.floor(timeSpent / 1000) : null,
    time_estimate_minutes: timeSpent ? Math.floor(timeSpent / (1000 * 60)) : null,
    time_estimate_hours: timeSpentHours,
    clickup_task_id: task.id ?? null,
    clickup_list_id: clickUpMapping.clickup_list_id ?? null,
    clickup_status_mapping: clickUpMapping.clickup_status_mapping ?? null,
  }
  const response = await fetch(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.TASKS.CREATE), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(taskData),
  })
  if (response.ok) {
    const result = await response.json()
    const taskId = result.task?.id ?? result.id
    if (task.attachments?.length > 0 && taskId) {
      await processClickUpAttachments(task.attachments, taskId)
    }
    if (task.time_spent && timeSpent) {
      await createClickUpTimeTracking(result, timeSpent, creatorId)
    }
  } else {
    const err = await response.json()
    console.error(`Erro ao criar tarefa ${task.name}:`, err)
  }
}

/**
 * Import Trello board cards into an existing project.
 */
export async function importTrelloToProject(
  trelloMapping: TrelloMappingData,
  projectId: number,
  onProgress?: (progress: ImportProgress) => void
): Promise<void> {
  const cards = trelloMapping.cards || []
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i]
    onProgress?.({ total: cards.length, current: i + 1, currentItem: card.name })
    await importTrelloCard(card, projectId, trelloMapping)
    await new Promise((r) => setTimeout(r, 100))
  }
}

/**
 * Import ClickUp list tasks into an existing project.
 */
export async function importClickUpToProject(
  clickUpMapping: ClickUpMappingData,
  projectId: number,
  onProgress?: (progress: ImportProgress) => void
): Promise<void> {
  const tasks = clickUpMapping.tasks || []
  for (let i = 0; i < tasks.length; i++) {
    const task = tasks[i]
    onProgress?.({ total: tasks.length, current: i + 1, currentItem: task.name })
    await importClickUpTask(task, projectId, clickUpMapping)
    await new Promise((r) => setTimeout(r, 100))
  }
}
